import React, { useEffect, useRef, useState } from 'react';
import { Animated, AccessibilityInfo, Easing, Pressable, Platform, StyleProp, TextStyle, View, ViewStyle } from 'react-native';
import { Text } from './AppText';
import { Ionicons } from '@expo/vector-icons';

import { Colors } from '../theme';
const IOS_EASE = Easing.bezier(0.22, 1, 0.36, 1);

/**
 * Keep motion delightful without overriding the user's accessibility choice.
 * The web path follows the browser preference; native follows iOS/Android's
 * Reduce Motion setting.
 */
export function useReducedMotion() {
  const [reducedMotion, setReducedMotion] = useState(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.matchMedia) {
      const query = window.matchMedia('(prefers-reduced-motion: reduce)');
      const onChange = (event: MediaQueryListEvent) => setReducedMotion(event.matches);
      setReducedMotion(query.matches);

      if (query.addEventListener) query.addEventListener('change', onChange);
      else query.addListener(onChange);

      return () => {
        if (query.removeEventListener) query.removeEventListener('change', onChange);
        else query.removeListener(onChange);
      };
    }

    let mounted = true;
    AccessibilityInfo.isReduceMotionEnabled().then(value => {
      if (mounted) setReducedMotion(value);
    });
    const subscription = AccessibilityInfo.addEventListener('reduceMotionChanged', setReducedMotion);
    return () => {
      mounted = false;
      subscription.remove();
    };
  }, []);

  return reducedMotion;
}

/**
 * Quiet, spring-like entrance used by cards and sections throughout the app.
 * The optional trigger lets dynamic lists replay their reveal after loading.
 */
export function FadeIn({
  children,
  delay = 0,
  duration = 460,
  distance = 12,
  trigger,
  style,
}: {
  children: React.ReactNode;
  delay?: number;
  duration?: number;
  distance?: number;
  trigger?: string | number;
  style?: StyleProp<ViewStyle>;
}) {
  const progress = useRef(new Animated.Value(0)).current;
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    progress.stopAnimation();
    progress.setValue(reducedMotion ? 1 : 0);
    if (reducedMotion) return;

    const animation = Animated.timing(progress, {
      toValue: 1,
      duration,
      delay,
      easing: IOS_EASE,
      useNativeDriver: Platform.OS !== 'web',
    });
    animation.start();
    return () => animation.stop();
  }, [delay, duration, progress, reducedMotion, trigger]);

  return (
    <Animated.View
      style={[
        style,
        {
          opacity: progress,
          transform: [
            { translateY: progress.interpolate({ inputRange: [0, 1], outputRange: [distance, 0] }) },
            { scale: progress.interpolate({ inputRange: [0, 1], outputRange: [0.985, 1] }) },
          ],
        },
      ]}
    >
      {children}
    </Animated.View>
  );
}

/** iOS-like press feedback for controls without changing their layout. */
export function PressableScale({
  children,
  onPress,
  onLongPress,
  style,
  containerStyle,
  contentStyle,
  disabled = false,
  hitSlop,
  accessibilityLabel,
  accessibilityHint,
  accessibilityState,
  accessibilityRole = 'button',
}: {
  children: React.ReactNode;
  onPress?: () => void;
  onLongPress?: () => void;
  style?: StyleProp<ViewStyle>;
  containerStyle?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
  disabled?: boolean;
  hitSlop?: { top?: number; bottom?: number; left?: number; right?: number };
  accessibilityLabel?: string;
  accessibilityHint?: string;
  accessibilityState?: { disabled?: boolean; selected?: boolean; checked?: boolean | 'mixed'; busy?: boolean; expanded?: boolean };
  accessibilityRole?: 'button' | 'tab' | 'link';
}) {
  const scale = useRef(new Animated.Value(1)).current;
  const reducedMotion = useReducedMotion();

  const animateTo = (toValue: number) => {
    if (reducedMotion) {
      scale.stopAnimation();
      scale.setValue(1);
      return;
    }

    Animated.spring(scale, {
      toValue: toValue === 1 ? 1 : 0.97,
      speed: toValue < 1 ? 38 : 25,
      bounciness: toValue < 1 ? 2 : 8,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  };
  const animatedStyle = contentStyle ?? style;

  return (
    <Pressable
      style={[containerStyle, { outlineStyle: 'none', outlineWidth: 0 } as any]}
      onPress={disabled ? undefined : onPress}
      onLongPress={disabled ? undefined : onLongPress}
      onPressIn={disabled ? undefined : () => animateTo(0.965)}
      onPressOut={disabled ? undefined : () => animateTo(1)}
      disabled={disabled}
      hitSlop={hitSlop}
      accessibilityRole={accessibilityRole}
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
      accessibilityState={{ ...accessibilityState, disabled: disabled || accessibilityState?.disabled }}
    >
      <Animated.View style={[animatedStyle, { transform: [{ scale }], opacity: disabled ? 0.48 : 1 }]}>
        {children}
      </Animated.View>
    </Pressable>
  );
}

/** Separates the dimmed backdrop fade from the sheet's upward motion. */
export function useSheetMotion(visible: boolean) {
  const progress = useRef(new Animated.Value(0)).current;
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    progress.stopAnimation();
    if (!visible) {
      progress.setValue(0);
      return;
    }

    if (reducedMotion) {
      progress.setValue(1);
      return;
    }

    progress.setValue(0);
    const animation = Animated.timing(progress, {
      toValue: 1,
      duration: 260,
      easing: IOS_EASE,
      useNativeDriver: Platform.OS !== 'web',
    });
    animation.start();
    return () => animation.stop();
  }, [progress, reducedMotion, visible]);

  return {
    backdrop: { opacity: progress },
    sheet: { transform: [{ translateY: progress.interpolate({ inputRange: [0, 1], outputRange: [28, 0] }) }] },
  };
}

/** Smoothly reveals monetary values when data loads or changes. */
export function AnimatedNumber({
  value,
  formatter = String,
  duration = 480,
  delay = 0,
  trigger,
  style,
}: {
  value: number;
  formatter?: (value: number) => string;
  duration?: number;
  delay?: number;
  trigger?: string | number;
  style?: StyleProp<TextStyle>;
}) {
  const progress = useRef(new Animated.Value(1)).current;
  const previousValue = useRef(0);
  const [displayValue, setDisplayValue] = useState(value);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    const from = previousValue.current;
    previousValue.current = value;
    progress.stopAnimation();

    if (reducedMotion || from === value) {
      progress.setValue(1);
      setDisplayValue(value);
      return;
    }

    progress.setValue(0);
    setDisplayValue(from);
    const listenerId = progress.addListener(({ value: ratio }) => {
      setDisplayValue(from + (value - from) * ratio);
    });
    const animation = Animated.timing(progress, {
      toValue: 1,
      duration,
      delay,
      easing: IOS_EASE,
      useNativeDriver: false,
    });
    animation.start(({ finished }) => {
      if (finished) setDisplayValue(value);
    });

    return () => {
      progress.removeListener(listenerId);
      animation.stop();
    };
  }, [delay, duration, progress, reducedMotion, trigger, value]);

  return <Text style={style}>{formatter(displayValue)}</Text>;
}

/** Horizontal progress fill with a restrained, native-feeling reveal. */
export function AnimatedProgress({
  value,
  color,
  trackStyle,
  fillStyle,
  duration = 650,
  delay = 0,
  trigger,
}: {
  value: number;
  color: string;
  trackStyle?: StyleProp<ViewStyle>;
  fillStyle?: StyleProp<ViewStyle>;
  duration?: number;
  delay?: number;
  trigger?: string | number;
}) {
  const progress = useRef(new Animated.Value(0)).current;
  const reducedMotion = useReducedMotion();
  const target = Math.max(0, Math.min(value, 100));

  useEffect(() => {
    progress.stopAnimation();
    if (reducedMotion) {
      progress.setValue(target);
      return;
    }

    progress.setValue(0);
    const animation = Animated.timing(progress, {
      toValue: target,
      duration,
      delay,
      easing: IOS_EASE,
      useNativeDriver: false,
    });
    animation.start();
    return () => animation.stop();
  }, [delay, duration, progress, reducedMotion, target, trigger]);

  return (
    <View style={[stylesMotion.progressTrack, trackStyle]}>
      <Animated.View
        style={[
          stylesMotion.progressFill,
          fillStyle,
          { backgroundColor: color, width: progress.interpolate({ inputRange: [0, 100], outputRange: ['0%', '100%'] }) },
        ]}
      />
    </View>
  );
}

/** One-shot success feedback for completed financial actions and empty states. */
export function SuccessPulse({
  size = 48,
  color = Colors.income,
}: {
  size?: number;
  color?: string;
}) {
  const progress = useRef(new Animated.Value(0)).current;
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    progress.stopAnimation();
    progress.setValue(reducedMotion ? 1 : 0);
    if (reducedMotion) return;

    const animation = Animated.timing(progress, {
      toValue: 1,
      duration: 520,
      easing: Easing.bezier(0.34, 1.56, 0.64, 1),
      useNativeDriver: Platform.OS !== 'web',
    });
    animation.start();
    return () => animation.stop();
  }, [progress, reducedMotion]);

  const iconSize = Math.round(size * 0.55);
  return (
    <Animated.View
      style={{
        width: size,
        height: size,
        transform: [{ scale: progress.interpolate({ inputRange: [0, 1], outputRange: [0.72, 1] }) }],
        opacity: progress,
      }}
    >
      <View
        style={{
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: color === Colors.income ? Colors.incomeLt : `${color}22`,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Ionicons name="checkmark" size={iconSize} color={color} />
      </View>
    </Animated.View>
  );
}

const stylesMotion = {
  progressTrack: { overflow: 'hidden', backgroundColor: Colors.surfaceAlt } as ViewStyle,
  progressFill: { height: '100%', borderRadius: 99 } as ViewStyle,
};
